"use client";

import { useRef } from "react";

/** 横方向 / 縦方向の drag を判別する閾値 (px) */
const DRAG_AXIS_LOCK_THRESHOLD = 6;

/**
 * 横スワイプ (例: embla) と Drawer の縦 swipe-dismiss の競合を解消するための hook。
 *
 * - pointerdown / touchstart 時点では伝播を止めない (Drawer が pending state を持つ)
 * - 最初の move で軸を判別。横優位なら以降の move を stopPropagation (Drawer の dismiss 抑止)
 * - 縦優位ならスルー (Drawer の dismiss は機能継続)
 *
 * Touch / Pointer の両方を見る。base-ui Drawer は両方の event を listen するため。
 * 一度軸を確定した後は同じ gesture 中は不可逆 (= 途中で方向転換しても初期軸を保持)。
 */
export function useHorizontalDragGuard() {
  const stateRef = useRef<{
    startX: number;
    startY: number;
    locked: "horizontal" | "vertical" | null;
  } | null>(null);

  const start = (x: number, y: number) => {
    stateRef.current = { startX: x, startY: y, locked: null };
  };
  const move = (e: { stopPropagation: () => void }, x: number, y: number) => {
    const s = stateRef.current;
    if (!s) return;
    if (s.locked === null) {
      const dx = Math.abs(x - s.startX);
      const dy = Math.abs(y - s.startY);
      if (dx > DRAG_AXIS_LOCK_THRESHOLD || dy > DRAG_AXIS_LOCK_THRESHOLD) {
        s.locked = dx > dy ? "horizontal" : "vertical";
      }
    }
    if (s.locked === "horizontal") {
      e.stopPropagation();
    }
  };
  const end = () => {
    stateRef.current = null;
  };

  return {
    onPointerDown: (e: React.PointerEvent) => start(e.clientX, e.clientY),
    onPointerMove: (e: React.PointerEvent) => move(e, e.clientX, e.clientY),
    onPointerUp: end,
    onPointerCancel: end,
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (t) start(t.clientX, t.clientY);
    },
    onTouchMove: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (t) move(e, t.clientX, t.clientY);
    },
    onTouchEnd: end,
    onTouchCancel: end,
  };
}
